import type { components } from '@connected/api-client/api.generated';

import type { Device, DeviceKind } from '../types/hook-types';

type CreateDeviceCommand = components['schemas']['CreateElectricalProjectDistributionBoardDeviceCommand'];
type EditDevice = components['schemas']['EditElectricalProjectDistributionBoardDeviceQueryResult']['Device'];

export type DeviceFactoryConfig = {
    electricalProjectDistributionBoardId: string;
    deviceKind: DeviceKind;
    enableExtendedRiso: boolean;
    existingDevices: Device[];
    sourceDevice?: EditDevice;
};

type RisoFields = {
    RisoSign: '>';
    RisoValue: 200;
};

const defaultDevices: Record<DeviceKind, Partial<EditDevice>> = {
    ResidualCurrentDevice: {
        ResidualCurrentDeviceType: null,
        RatedCurrent: null,
        TripDelay: null,
        IsTestButtonFunctional: null,
    },
    ResidualCurrentCircuitBreakerDevice: {
        ResidualCurrentDeviceType: null,
        RatedCurrent: null,
        TrippingCharacteristic: null,
        BreakerRatedCurrent: null,
        TripDelay: null,
        IsTestButtonFunctional: null,
    },
    FinalCircuitProtectiveDevice: {
        PowerPhaseKind: null,
        TrippingCharacteristic: null,
        BreakerRatedCurrent: null,
    },
};

/**
 * Calculate the next order number for a new device.
 * Uses the maximum existing order + 1 to handle gaps from deleted devices.
 * Returns 1 if no existing devices (handles empty array edge case).
 */
export function calculateNextOrder(existingDevices: Device[], offset: number = 0): number {
    if (existingDevices.length === 0) {
        return 1 + offset;
    }
    const maxOrder = Math.max(...existingDevices.map(d => d.Order ?? 0));
    return maxOrder + 1 + offset;
}

/**
 * Get kind-specific default field values for a device.
 */
export function getDeviceDefaults(kind: DeviceKind): Partial<EditDevice> {
    return defaultDevices[kind];
}

/**
 * Get Riso fields based on device kind and enableExtendedRiso flag.
 *
 * Rules:
 * - ResidualCurrentDevice: Always include Riso fields (when enableExtendedRiso is false)
 * - ResidualCurrentCircuitBreakerDevice: Always include Riso fields (when enableExtendedRiso is false)
 * - FinalCircuitProtectiveDevice: Only include Riso fields when enableExtendedRiso is false
 *
 * When enableExtendedRiso is true, Riso is handled on a separate page.
 */
export function getRisoFields(
    kind: DeviceKind,
    enableExtendedRiso: boolean,
): RisoFields | null {
    // When extended Riso is enabled, there's a separate page for measurements
    if (enableExtendedRiso) {
        return null;
    }

    // All device kinds get Riso fields when not using extended page
    return { RisoSign: '>', RisoValue: 200 };
}

/**
 * Create a single device payload with all required fields.
 * This is the main factory function that composes all the building blocks.
 */
export function createDevicePayload(
    config: DeviceFactoryConfig,
    orderOffset: number = 0,
): CreateDeviceCommand {
    const {
        electricalProjectDistributionBoardId,
        deviceKind,
        enableExtendedRiso,
        existingDevices,
        sourceDevice,
    } = config;

    // Filter devices by kind and board for accurate order calculation
    const filteredDevices = existingDevices.filter(
        d => d.DeviceKind === deviceKind
            && d.ElectricalProjectDistributionBoardId === electricalProjectDistributionBoardId,
    );

    const baseDevice = sourceDevice ?? getDeviceDefaults(deviceKind);
    const order = calculateNextOrder(filteredDevices, orderOffset);
    const risoFields = getRisoFields(deviceKind, enableExtendedRiso);

    return {
        ...baseDevice,
        ElectricalProjectDistributionBoardDeviceId: crypto.randomUUID(),
        ElectricalProjectDistributionBoardId: electricalProjectDistributionBoardId,
        DeviceKind: sourceDevice?.DeviceKind ?? deviceKind,
        Order: order,
        Identifier: `Nr ${String(order)}`,
        ...risoFields,
    };
}

/**
 * Create multiple device payloads for batch creation (e.g., copy operation).
 * Each device gets a sequential order number.
 */
export function createDevicePayloads(
    config: DeviceFactoryConfig,
    count: number = 1,
): CreateDeviceCommand[] {
    return Array.from({ length: count }, (_, index) =>
        createDevicePayload(config, index));
}
